// 教育費計算のためのユーティリティ関数

// 教育費テンプレート
export const EDUCATION_COSTS = {
  public: {
    elementary: 2112000, // 小学校 211.2万円
    junior: 1616000,     // 中学校 161.6万円
    senior: 1543000,     // 高校 154.3万円
    university: 10000000 // 大学 1000万円
  },
  private: {
    elementary: 3500000, // 私立小学校（推定）
    junior: 2800000,     // 私立中学校（推定）
    senior: 2500000,     // 私立高校（推定）
    university: 15000000 // 私立大学（推定）
  }
};

// 投資商品の想定利回り
export const INVESTMENT_RETURNS = {
  savings: 0.002,        // 普通預金 0.2%
  timeDeposit: 0.005,    // 定期預金 0.5%
  gakushiInsurance: 0.07, // 学資保険 7%（返戻率107%想定）
  nisa: 0.05,            // つみたてNISA 5%
  juniorNisa: 0.05,      // ジュニアNISA 5%
  indexFund: 0.05,       // インデックスファンド 5%
  balanceFund: 0.04,     // バランスファンド 4%
  foreignStock: 0.065,   // 外国株式ファンド 6.5%
  bondFund: 0.02,        // 債券ファンド 2%
  individualStock: 0.08  // 個別株投資 8%（高リスク）
};

// 年齢制限
export const AGE_LIMITS = {
  juniorNisa: 18,        // ジュニアNISA引き出し制限
  educationGift: 30      // 教育資金贈与制度
};

// 贈与税非課税枠
export const GIFT_TAX_LIMITS = {
  annual: 1100000,       // 暦年贈与 110万円
  education: 15000000,   // 教育資金一括贈与 1500万円
  schoolDirect: 15000000, // 学校等直接支払い 1500万円
  cram: 5000000         // 学習塾等 500万円
};

// 複利計算
export const calculateCompoundInterest = (principal, rate, years, monthlyContribution = 0) => {
  if (rate === 0) {
    return principal + (monthlyContribution * 12 * years);
  }
  
  const monthlyRate = rate / 12;
  const totalMonths = years * 12;
  
  // 元本の複利計算
  const principalGrowth = principal * Math.pow(1 + rate, years);
  
  // 毎月積立の複利計算
  let contributionGrowth = 0;
  if (monthlyContribution > 0) {
    contributionGrowth = monthlyContribution * 
      ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
  }
  
  return Math.round(principalGrowth + contributionGrowth);
};

// 教育段階までの年数計算
export const calculateYearsToEducationStage = (currentAge, stage) => {
  const stageAges = {
    elementary: 6,
    junior: 12,
    senior: 15,
    university: 18
  };
  
  const targetAge = stageAges[stage];
  return Math.max(0, targetAge - currentAge);
};

// 必要貯蓄額計算（各段階まで）
export const calculateRequiredSavings = (currentAge, schoolType = 'public') => {
  const costs = EDUCATION_COSTS[schoolType];
  const stages = ['elementary', 'junior', 'senior', 'university'];
  
  return stages.map(stage => {
    const yearsToStage = calculateYearsToEducationStage(currentAge, stage);
    const cumulativeCost = stages
      .slice(0, stages.indexOf(stage) + 1)
      .reduce((sum, s) => sum + costs[s], 0);
    
    return {
      stage,
      yearsToStage,
      stageCost: costs[stage],
      cumulativeCost,
      isReached: yearsToStage === 0
    };
  });
};

// 最適な貯蓄配分提案
export const suggestOptimalAllocation = (monthlyBudget, currentSavings, currentAge, riskTolerance = 'medium') => {
  const suggestions = [];
  
  // リスク許容度に応じた配分
  const allocations = {
    conservative: {
      savings: 0.3,
      timeDeposit: 0.2,
      gakushiInsurance: 0.3,
      nisa: 0.2
    },
    medium: {
      savings: 0.1,
      timeDeposit: 0.1,
      gakushiInsurance: 0.2,
      nisa: 0.4,
      indexFund: 0.2
    },
    aggressive: {
      savings: 0.05,
      nisa: 0.3,
      indexFund: 0.3,
      foreignStock: 0.25,
      individualStock: 0.1
    }
  };
  
  const allocation = allocations[riskTolerance];
  
  Object.entries(allocation).forEach(([investment, ratio]) => {
    const monthlyAmount = Math.round(monthlyBudget * ratio);
    const yearsToUniversity = calculateYearsToEducationStage(currentAge, 'university');
    const expectedReturn = calculateCompoundInterest(
      currentSavings * ratio,
      INVESTMENT_RETURNS[investment],
      yearsToUniversity,
      monthlyAmount
    );
    
    suggestions.push({
      investment,
      monthlyAmount,
      ratio,
      expectedReturn,
      riskLevel: investment === 'savings' || investment === 'timeDeposit' ? 'low' :
                investment === 'gakushiInsurance' || investment === 'nisa' ? 'medium' : 'high'
    });
  });
  
  return suggestions.sort((a, b) => b.expectedReturn - a.expectedReturn);
};

// 目標達成可能性チェック
export const checkGoalAchievability = (currentSavings, monthlyBudget, currentAge, targetAmount, schoolType = 'public') => {
  const yearsToUniversity = calculateYearsToEducationStage(currentAge, 'university');
  const totalCosts = Object.values(EDUCATION_COSTS[schoolType]).reduce((sum, cost) => sum + cost, 0);
  const actualTarget = targetAmount || totalCosts;
  
  // 異なる投資戦略での達成可能性
  const strategies = [
    { name: '安全重視', rate: 0.01, description: '預金・学資保険中心' },
    { name: 'バランス', rate: 0.04, description: 'NISA・投資信託併用' },
    { name: '積極投資', rate: 0.06, description: '株式投資中心' }
  ];
  
  return strategies.map(strategy => {
    const projectedAmount = calculateCompoundInterest(
      currentSavings,
      strategy.rate,
      yearsToUniversity,
      monthlyBudget
    );
    
    const achievementRate = (projectedAmount / actualTarget) * 100;
    const shortfall = Math.max(0, actualTarget - projectedAmount);
    const requiredMonthly = shortfall > 0 ? 
      Math.ceil(shortfall / (yearsToUniversity * 12)) : 0;
    
    return {
      ...strategy,
      projectedAmount,
      achievementRate: Math.round(achievementRate),
      isAchievable: achievementRate >= 100,
      shortfall,
      requiredMonthly
    };
  });
};

// 税制優遇活用提案
export const suggestTaxBenefits = (totalEducationCosts, hasGrandparents = false) => {
  const suggestions = [];
  
  if (hasGrandparents) {
    suggestions.push({
      type: 'direct_payment',
      title: '教育費直接支払い（推奨）',
      description: '祖父母が学校に直接支払う場合、贈与税はかかりません',
      maxAmount: totalEducationCosts,
      riskLevel: 'none',
      priority: 1
    });
    
    if (totalEducationCosts <= GIFT_TAX_LIMITS.education) {
      suggestions.push({
        type: 'education_gift',
        title: '教育資金一括贈与',
        description: '30歳までに使い切る必要があります',
        maxAmount: GIFT_TAX_LIMITS.education,
        riskLevel: 'low',
        priority: 2,
        warning: '1,500万円を超える場合は税金がかかります'
      });
    }
    
    suggestions.push({
      type: 'annual_gift',
      title: '暦年贈与',
      description: '毎年110万円まで非課税で贈与可能',
      maxAmount: GIFT_TAX_LIMITS.annual,
      riskLevel: 'none',
      priority: 3
    });
  }
  
  return suggestions.sort((a, b) => a.priority - b.priority);
};

// 進捗追跡用データ構造
export const createProgressTracker = (targetAmount, currentAge) => {
  const yearsToUniversity = calculateYearsToEducationStage(currentAge, 'university');
  const milestones = [];
  
  // 年次マイルストーン作成
  for (let year = 1; year <= yearsToUniversity; year++) {
    const targetSavings = (targetAmount / yearsToUniversity) * year;
    milestones.push({
      year,
      targetAmount: Math.round(targetSavings),
      actualAmount: 0,
      isAchieved: false,
      childAge: currentAge + year
    });
  }
  
  return {
    targetAmount,
    currentAmount: 0,
    yearsRemaining: yearsToUniversity,
    milestones,
    lastUpdated: new Date().toISOString()
  };
};


